import React from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import { cssMap, cx } from '@atlaskit/css';
import { Anchor, Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { HierarchyPerson } from '../../../types';

/** Number of avatars shown stacked in the "See N more" row before they are visually clipped. */
const MAX_STACKED_AVATARS = 3;

const messages = defineMessages({
	seeMore: {
		id: 'user-profile-card.MiniHierarchyMoreRow.seeMore',
		defaultMessage: 'See {count} more',
		description:
			'Label on the row that links to the full reporting line when a user has more direct reports than fit on the profile card',
	},
});

const styles = cssMap({
	row: {
		display: 'block',
		width: '100%',
		paddingTop: token('space.075'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.medium'),
		color: token('color.text'),
		textDecoration: 'none',
	},
	interactiveRow: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			textDecoration: 'none',
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	stackedAvatar: {
		marginInlineStart: token('space.negative.100'),
	},
	firstAvatar: {
		marginInlineStart: token('space.0'),
	},
});

interface MiniHierarchyMoreRowProps {
	/** The direct reports that did not fit inline; their avatars are stacked as a preview. */
	people: HierarchyPerson[];
	/** Profile-page URL for the full reporting line. */
	href?: string;
	target?: string;
	onClick?: () => void;
	onNavigate?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
	testId?: string;
}

/**
 * The "See N more" row shown at the bottom of the direct-reports section when a user has more direct
 * reports than fit on the card. Renders a compact stack of the overflow avatars plus a "See N more"
 * label, and links to the full reporting line on the profile page (same destination as the pane's
 * "View full reporting line" button).
 */
export function MiniHierarchyMoreRow({
	people,
	href,
	target,
	onClick,
	onNavigate,
	testId,
}: MiniHierarchyMoreRowProps): React.JSX.Element {
	const intl = useIntl();
	const stacked = people.slice(0, MAX_STACKED_AVATARS);

	const content = (
		<Inline space="space.100" alignBlock="center">
			<Inline alignBlock="center">
				{stacked.map((person, index) => (
					<Box
						key={person.accountId}
						xcss={index === 0 ? styles.firstAvatar : styles.stackedAvatar}
						aria-hidden
					>
						<Avatar src={person.avatarUrl ?? undefined} name={person.name} size="small" />
					</Box>
				))}
			</Inline>
			<Text size="small" weight="medium">
				{intl.formatMessage(messages.seeMore, { count: people.length })}
			</Text>
		</Inline>
	);

	if (!href) {
		return (
			<Box xcss={styles.row} testId={testId}>
				{content}
			</Box>
		);
	}

	return (
		<Anchor
			href={href}
			target={target}
			rel={target === '_blank' ? 'noopener noreferrer' : undefined}
			onClick={(event) => {
				onClick?.();
				onNavigate?.(event);
			}}
			xcss={cx(styles.row, styles.interactiveRow)}
			testId={testId}
		>
			{content}
		</Anchor>
	);
}
